import type { SprintDraftBody, SprintEdit } from '../../api/endpoints';
import type { Sprint } from '../../api/types';
import { parseCalendarDate } from '../../lib/format';

/**
 * PH-23 — the sprint form's model, with no React in it.
 *
 * ⭐⭐ WHY THIS IS A SEPARATE FILE. Everything here is a claim about the API contract — which fields
 * are sent, which are omitted, what `null` means, and which drafts the API would refuse. Those are
 * exactly the claims worth pinning with a test that needs no DOM, and exactly the ones that rot
 * silently inside a component.
 *
 * 🔴 DATES ARE CALENDAR DATES AND NEVER BECOME `Date` OBJECTS ON THE WAY THROUGH.
 *   `starts_on`/`ends_on` are Postgres DATE, cast `::text`, and arrive as plain `YYYY-MM-DD`. An
 *   `<input type="date">` reads and writes exactly that same string. ⇒ The draft holds the API's own
 *   strings, hands them back unchanged, and no `new Date(iso)` appears anywhere in the round trip.
 *   ⛔ `formatDate` does not exist any more (PH-21) precisely because it could not tell a calendar
 *   date from an instant; see the header of `lib/format.ts`. The only two places this file builds a
 *   `Date` at all are `todayCalendarDate` and `addCalendarDays`, and both say why.
 */

export interface SprintDraft {
  name: string;
  goal: string;
  /**
   * `''` means NO VERSION.
   *
   * ⚠️ Not `null`, because a `<select>`'s value is always a string and mapping the empty option to
   * null inside the component would put the API's null/absent distinction in the one place it cannot
   * be tested. The translation happens once, in `createSprintBody`/`sprintEditBody`, where it is.
   */
  versionId: string;
  /** `YYYY-MM-DD`, or `''` while the field is empty. */
  startsOn: string;
  endsOn: string;
}

export const NO_VERSION = '';

/**
 * A new sprint's default length. ⭐ A starting point in the two date fields, not a rule — the user
 * changes either one and the API stores whatever they choose. Two weeks is the commonest sprint and
 * is a strictly better default than two blank fields on a 390px screen.
 */
export const DEFAULT_SPRINT_LENGTH_DAYS = 14;

/**
 * Today, on the READER'S calendar.
 *
 * 🔴 THE LOCAL GETTERS ARE THE POINT, and the obvious one-liner is the bug.
 *   `new Date().toISOString().slice(0, 10)` takes the UTC date — so in Asia/Bangkok, where this team
 *   works, every sprint created before 07:00 would default to YESTERDAY, and west of Greenwich every
 *   sprint created after 19:00 would default to TOMORROW. ⇒ `getFullYear`/`getMonth`/`getDate`,
 *   which are the local-calendar getters and are the correct answer to a local-calendar question.
 *
 * ⭐ THIS IS NOT THE `formatCalendarDate` CASE AND MUST NOT BE "FIXED" TO MATCH IT. That function
 *   renders a day the SERVER named, so it pins itself to UTC to avoid re-interpreting it. This one
 *   ASKS what day it is where the user is standing, which is genuinely a local question. Two
 *   opposite-looking rules, one principle: never let a timezone silently change which day is meant.
 *
 * ⚠️ `now` is a parameter only so a test can pin an instant and a zone. Production passes nothing.
 */
export function todayCalendarDate(now: Date = new Date()): string {
  return formatParts(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

/**
 * `2026-08-17` + 14 → `2026-08-31`.
 *
 * ⭐ `Date.UTC` FROM THE PARTS, never `new Date(string)` — the same move `formatCalendarDate` makes,
 * and for the same reason: parsing the string would pick a timezone, and adding days across a DST
 * boundary in local time can land on the wrong side of a midnight. In UTC every day is 24 hours, so
 * the arithmetic is exact. ⭐ `Date.UTC` normalises overflow itself, so month and year ends need no
 * special case.
 *
 * ⚠️ Returns `null` for anything that is not a plain `YYYY-MM-DD` rather than guessing — the caller
 * decides what to do about a field the user has not filled in yet.
 */
export function addCalendarDays(date: string, days: number): string | null {
  const parts = parseCalendarDate(date);
  if (!parts) return null;
  const shifted = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
  return formatParts(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate());
}

function formatParts(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** A new sprint, dated from today. ⛔ The name is blank on purpose: only the team knows it. */
export function blankDraft(today: string): SprintDraft {
  return {
    name: '',
    goal: '',
    versionId: NO_VERSION,
    startsOn: today,
    endsOn: addCalendarDays(today, DEFAULT_SPRINT_LENGTH_DAYS) ?? today,
  };
}

/**
 * An existing sprint, as a draft.
 *
 * ⭐ `starts_on`/`ends_on` go through UNTOUCHED. They are already the `YYYY-MM-DD` an
 * `<input type="date">` wants, and anything done to them on the way — formatting, parsing,
 * re-serialising — is a chance to move the day.
 */
export function draftOf(sprint: Sprint): SprintDraft {
  return {
    name: sprint.name,
    goal: sprint.goal ?? '',
    versionId: sprint.version_id ?? NO_VERSION,
    startsOn: sprint.starts_on,
    endsOn: sprint.ends_on,
  };
}

/** The API's own limits, named here so the messages below cannot drift from the schema. */
export const SPRINT_NAME_MAX = 200;
export const SPRINT_GOAL_MAX = 2000;

/**
 * Why the API would refuse this draft, or `null`.
 *
 * ⛔ THIS IS NOT THE VALIDATION — the API is, and it is re-checked there whatever this says. What
 * this buys is that a refusal the client can already see does not have to cost a round trip and come
 * back as `the request body or query was not valid`, which is the FIXED generic message the API's
 * `validationFailed()` is built to return. That message is deliberately uninformative (it cannot
 * echo input, by construction), so a zod refusal reaching the user is a refusal nobody can act on.
 *
 * ⭐ ONE message at a time, the first problem in reading order. A list of five red lines on a 390px
 * screen pushes the button off the bottom.
 */
export function draftProblem(draft: SprintDraft): string | null {
  const name = draft.name.trim();
  if (name === '') return 'A sprint needs a name.';
  if (name.length > SPRINT_NAME_MAX) {
    return `A sprint name is at most ${SPRINT_NAME_MAX} characters — this one is ${name.length}.`;
  }
  if (draft.goal.trim().length > SPRINT_GOAL_MAX) {
    return `A sprint goal is at most ${SPRINT_GOAL_MAX} characters — this one is ${draft.goal.trim().length}.`;
  }
  if (!parseCalendarDate(draft.startsOn)) return 'Choose a start date.';
  if (!parseCalendarDate(draft.endsOn)) return 'Choose an end date.';
  /**
   * ⭐ THE SAME EXPRESSION THE API USES — `input.endsOn < input.startsOn`, a plain string compare.
   *   On `YYYY-MM-DD` lexicographic order IS chronological order, which is the property the format
   *   was chosen for. ⛔ Not a `Date` subtraction: that would reintroduce a parse this file exists to
   *   avoid, to answer a question the strings already answer.
   * ⚠️ Equal dates are ALLOWED, by the API (`endsOn < startsOn`) and by db/001's
   *   `sprint_dates_ordered`. A one-day sprint is legal and this must not be stricter than the
   *   server — a client-side rule the API does not have is a feature nobody can use and nobody can
   *   find the reason for.
   */
  if (draft.endsOn < draft.startsOn) {
    return 'The end date is before the start date — the API refuses that, and db/001 does too.';
  }
  return null;
}

/**
 * The CREATE body.
 *
 * ⛔ Optional fields are OMITTED, not sent as `null`. Identical to the API (`goal` and `versionId`
 * are `.nullish()` on create), and it keeps the request honest about what the user filled in — the
 * same rule `closeSprint` follows for `carryOverToSprintId`.
 * ⭐ The name is TRIMMED. `'  '` passes `z.string().min(1)` and would create a sprint whose header
 * renders as an empty gap.
 */
export function createSprintBody(draft: SprintDraft): SprintDraftBody {
  const goal = draft.goal.trim();
  return {
    name: draft.name.trim(),
    startsOn: draft.startsOn,
    endsOn: draft.endsOn,
    ...(goal === '' ? {} : { goal }),
    ...(draft.versionId === NO_VERSION ? {} : { versionId: draft.versionId }),
  };
}

/**
 * The EDIT body: ONLY what actually changed.
 *
 * 🔴 A DIFF, NOT THE WHOLE FORM, AND THAT IS A CORRECTNESS CHOICE RATHER THAN A SAVING. `PATCH
 *   /sprints/:id` is partial, so sending every field means a person who only fixed a typo in the name
 *   also writes back the dates AS THEY WERE WHEN THEIR FORM OPENED — silently undoing whatever
 *   somebody else changed in between. The diff makes a concurrent edit a merge instead of a
 *   last-writer-wins.
 *
 * ⭐ CLEARING IS EXPRESSED AS `null`, KEEPING IS EXPRESSED AS ABSENCE, because that is the
 *   distinction the API's `.nullable()` (not `.nullish()`) PATCH schema draws. Emptying the goal box
 *   must send `goal: null`; leaving it alone must send no `goal` key at all.
 *
 * ⚠️ An EMPTY object is a legitimate result and means nothing changed. The caller decides what to do
 *   about that — see `useSprintEditor`, which declines to send it. (The API would accept it: with no
 *   assignments `updateSprint` skips the UPDATE entirely and returns the row. So this is about not
 *   reporting a save that saved nothing, not about avoiding a refusal.)
 */
export function sprintEditBody(sprint: Sprint, draft: SprintDraft): SprintEdit {
  const name = draft.name.trim();
  const goal = draft.goal.trim();
  const versionId = draft.versionId === NO_VERSION ? null : draft.versionId;

  return {
    ...(name === sprint.name ? {} : { name }),
    ...(goal === (sprint.goal ?? '') ? {} : { goal: goal === '' ? null : goal }),
    ...(versionId === (sprint.version_id ?? null) ? {} : { versionId }),
    ...(draft.startsOn === sprint.starts_on ? {} : { startsOn: draft.startsOn }),
    ...(draft.endsOn === sprint.ends_on ? {} : { endsOn: draft.endsOn }),
  };
}

export function isEmptyEdit(edit: SprintEdit): boolean {
  return Object.keys(edit).length === 0;
}
